/**
 * ColorInput - Reusable color picker with presets and custom color support
 *
 * Features:
 * - "No color" option
 * - Preset color swatches
 * - Custom color picker (expandable)
 * - Checkmark indicator on selected color
 * - Ring styling for selection
 */
import type { ReactElement } from 'react';
import { useState } from 'react';
import { IoCheckmark, IoColorPalette } from 'react-icons/io5';

// ============================================================================
// Types
// ============================================================================

export interface ColorInputProps {
  /** Currently selected color (hex string or null for no color) */
  value: string | null;
  /** Called when color changes */
  onChange: (color: string | null) => void;
  /** Preset colors to display */
  presets?: string[];
  /** Whether custom color picker is enabled */
  allowCustom?: boolean;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Label for the field */
  label?: string;
  /** Whether the label should indicate optional */
  optional?: boolean;
  /** ID for aria-labelledby */
  id?: string;
  /** `touch` gives every control a 48px touch target (Game Companion). */
  size?: 'default' | 'touch';
}

// ============================================================================
// Default Colors
// ============================================================================

/**
 * Extended preset colors palette including MTG land colors
 */
export const DEFAULT_PRESET_COLORS = [
  // MTG land colors
  '#f9faf4', // White (Plains)
  '#0e68ab', // Blue (Island)
  '#150b00', // Black (Swamp)
  '#d3202a', // Red (Mountain)
  '#00733e', // Green (Forest)
  // Additional colors
  '#6366f1', // Indigo (aether)
  '#8b5cf6', // Violet
  '#a855f7', // Purple
  '#ec4899', // Pink
  '#f43f5e', // Rose
  '#f97316', // Orange
  '#f59e0b', // Amber
  '#eab308', // Yellow
  '#84cc16', // Lime
  '#14b8a6', // Teal
  '#06b6d4', // Cyan
  '#3b82f6', // Blue
  '#6b7280', // Gray
];

// ============================================================================
// Component
// ============================================================================

export default function ColorInput({
  value,
  onChange,
  presets = DEFAULT_PRESET_COLORS,
  allowCustom = true,
  disabled = false,
  label,
  optional = false,
  id,
  size = 'default',
}: ColorInputProps): ReactElement {
  const swatchSize = size === 'touch' ? 'h-12 w-12' : 'h-8 w-8';
  // Determine if current value is a custom color (not in presets and not null)
  const isCustomColor = value !== null && !presets.includes(value);

  const [showCustomPicker, setShowCustomPicker] = useState(isCustomColor);

  return (
    <div>
      {label && (
        <label id={id ? `${id}-label` : undefined} className="text-silver mb-1.5 block text-sm font-medium">
          {label}
          {optional && <span className="text-ash ml-1">(optional)</span>}
        </label>
      )}

      <div
        role="radiogroup"
        aria-labelledby={id ? `${id}-label` : undefined}
        aria-label={label || 'Color selection'}
        className="flex flex-wrap gap-2"
      >
        {/* No color option */}
        <button
          type="button"
          role="radio"
          aria-checked={value === null}
          aria-label="No color"
          onClick={() => {
            onChange(null);
            setShowCustomPicker(false);
          }}
          disabled={disabled}
          className={`border-storm flex ${swatchSize} items-center justify-center rounded-full border-2 transition-all ${
            value === null ? 'ring-aether ring-offset-obsidian ring-2 ring-offset-2' : 'hover:border-ash'
          } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
        >
          {value === null && <IoCheckmark className="text-pearl h-4 w-4" />}
        </button>

        {/* Preset colors */}
        {presets.map((presetColor) => (
          <button
            key={presetColor}
            type="button"
            role="radio"
            aria-checked={value === presetColor}
            aria-label={`Color ${presetColor}`}
            onClick={() => {
              onChange(presetColor);
              setShowCustomPicker(false);
            }}
            disabled={disabled}
            className={`flex ${swatchSize} items-center justify-center rounded-full transition-all ${
              value === presetColor ? 'ring-aether ring-offset-obsidian ring-2 ring-offset-2' : 'hover:scale-110'
            } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
            style={{ backgroundColor: presetColor }}
          >
            {value === presetColor && <IoCheckmark className="h-4 w-4 text-white drop-shadow" />}
          </button>
        ))}

        {/* Custom color toggle */}
        {allowCustom && (
          <button
            type="button"
            aria-label="Custom color"
            aria-expanded={showCustomPicker}
            onClick={() => setShowCustomPicker(!showCustomPicker)}
            disabled={disabled}
            className={`border-storm text-ash hover:text-pearl flex ${swatchSize} items-center justify-center rounded-full border-2 transition-colors ${
              showCustomPicker || isCustomColor ? 'border-aether text-pearl' : ''
            } ${isCustomColor ? 'ring-aether ring-offset-obsidian ring-2 ring-offset-2' : ''} ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
          >
            <IoColorPalette className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Custom color input */}
      {allowCustom && showCustomPicker && (
        <div className="mt-3 flex items-center gap-3">
          <input
            type="color"
            value={value ?? '#6366f1'}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className={`${size === 'touch' ? 'h-12' : 'h-10'} w-20 cursor-pointer rounded border-0 bg-transparent disabled:cursor-not-allowed disabled:opacity-50`}
            aria-label="Color picker"
          />
          <input
            type="text"
            value={value ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              // Allow partial hex input while typing
              if (val === '' || /^#[0-9A-Fa-f]{0,6}$/.test(val)) {
                onChange(val || null);
              }
            }}
            placeholder="#000000"
            disabled={disabled}
            className={`border-storm bg-slate text-pearl placeholder:text-ash focus-ring w-28 rounded-lg border px-3 py-2 font-mono text-sm disabled:cursor-not-allowed disabled:opacity-50 ${size === 'touch' ? 'min-h-12' : ''}`}
            aria-label="Hex color code"
          />
        </div>
      )}
    </div>
  );
}
